import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {SpinnerService} from '../shared/services/spinner.service';
import {ToastrService} from 'ngx-toastr';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, map, switchMap} from 'rxjs/operators';
import {UserApiService} from '../api/user-api.service';
import {EventCommitteeMember, EventDTO, EventEmailMessageDTO, EventFormQuestion, EventParticipantDTO, EventParticipantType, EventService, EventSpeaker, EventTicketOption, isStripePaymentEnabled} from '../api/event-api.service';
import {toHTML} from 'ngx-editor';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {cleanQuestions, defaultAttendingQuestion} from '../registration-questions/registration-questions.util';
import {RoleScope, reachesAllChapters, reachesChapter, storedRoleScope} from '../shared/access';

interface TicketOptionPanel {
    label: string;
    options: EventTicketOption[];
    groupRequired?: boolean;
}

declare const $: any;

@Component({
    selector: 'app-chapter-event',
    templateUrl: './chapter-event.component.html',
    styleUrls: ['./chapter-event.component.css']
})
export class ChapterEventComponent implements OnInit {
    @ViewChild('addEventModal') addEventModalRef!: ElementRef;
    @ViewChild('editEventModal') editEventModalRef!: ElementRef;
    @ViewChild('addEmailModal') addEmailModalRef!: ElementRef;
    events: EventDTO[] = [];
    selectedEvent: EventDTO | null = null;
    newEvent: EventDTO = this.initializeNewEvent();

    page: number = 1;

    chapterId: string = '';
    userRole: any;
    userChapter: any;
    userId: string = '';
    userGroup: string = '';
    roleScope: RoleScope = storedRoleScope();

    /** True for roles whose reach is every chapter (system administrator and the NEC tier), as decided by the API. */
    get allChapters(): boolean {
        return reachesAllChapters(this.roleScope);
    }

    newEmail: EventEmailMessageDTO = this.initializeNewEmail();
    newEventPanels: TicketOptionPanel[] = [];
    selectedEventPanels: TicketOptionPanel[] = [];
    // Organizer-defined registration questions (radio/checkbox) for add / edit.
    newEventQuestions: EventFormQuestion[] = [];
    selectedEventQuestions: EventFormQuestion[] = [];
    // Events created before this feature have no registrationQuestions and use the built-in attending radio.
    // The editor shows the default question for them, but nothing is sent unless the organizer changes it.
    selectedQuestionsLegacy = false;
    selectedQuestionsDirty = false;
    newEventCcEmailsInput: string = '';
    editEventCcEmailsInput: string = '';

    // -------------------- Event Committee / Guests of Honor / Speakers --------------------
    // Backed by the EventParticipant table rather than embedded on Event (see event-api.service.ts).
    // The add-event modal still edits newEvent.eventCommittee/etc. directly (there's no eventId yet
    // to attach participants to until the event is created), but the edit modal uses these dedicated
    // arrays, fetched from the participants endpoint, with `id` tracked for reconciliation on save.
    chapterMembers: { id: string; name: string; email: string }[] = [];
    editEventCommittee: (EventCommitteeMember & { id?: string })[] = [];
    editEventGuestsOfHonor: (EventCommitteeMember & { id?: string })[] = [];
    editEventSpeakers: (EventSpeaker & { id?: string })[] = [];
    private committeeAtLoad: (EventCommitteeMember & { id?: string })[] = [];
    private guestsAtLoad: (EventCommitteeMember & { id?: string })[] = [];
    private speakersAtLoad: (EventSpeaker & { id?: string })[] = [];

    constructor(
        private route: ActivatedRoute,
        private eventService: EventService,
        private spinner: SpinnerService,
        private toastr: ToastrService,
        private userApiService: UserApiService,
        private router: Router
    ) {
    }

    get isNecExecutive(): boolean {
        return this.allChapters;
    }


    viewEvent(event: any) {
        if (event?.id) {
            this.router.navigate(['/event-home', event.id]);
        }
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            this.chapterId = params.get('id') || '';
            //this.username = sessionStorage.getItem('username') || '';
            this.userId = sessionStorage.getItem('userId') || '';
            this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
            this.userChapter = sessionStorage.getItem('chapter') || '';
            this.userGroup = sessionStorage.getItem('userGroup') || '';
            this.roleScope = storedRoleScope();
            this.newEvent.eventScope = this.isNecExecutive ? 'NEC' : 'CHAPTER';
            this.loadEvents(this.chapterId);
            this.loadChapterMembersForPicker();

        });
    }

    // Deliberately org-wide, not scoped to this page's chapter: any RANA member can be linked as
    // event committee regardless of their own chapterAffiliation (an NEC event in particular has no
    // single "home" chapter its committee should be drawn from). Scoping this to getUsersByChapterId
    // silently excluded members from other chapters from the search results, so a name typed without
    // a matching option saved with no linked account and no elevated permission.
    private loadChapterMembersForPicker(): void {
        this.userApiService.getUsers().subscribe({
            next: (users: any[]) => {
                this.chapterMembers = (users || []).map(u => ({id: u.id, name: `${u.firstname} ${u.lastname}`, email: u.email || ''}));
            },
            error: () => {
            }
        });
    }

    openEditEvent(event: any) {
        event.stripePaymentEnabled = isStripePaymentEnabled(event);
        this.selectedEvent = event;
        this.selectedEventPanels = this.buildPanelsFromOptions(event.ticketOptions || []);
        this.selectedQuestionsLegacy = event.registrationQuestions == null;
        this.selectedQuestionsDirty = false;
        this.selectedEventQuestions = event.registrationQuestions != null
            ? JSON.parse(JSON.stringify(event.registrationQuestions))
            : (event.eventCategory === 'CONFERENCE' ? [defaultAttendingQuestion()] : []);
        this.editEventCcEmailsInput = (event.ccEmailIds || []).join(', ');
        this.editEventCommittee = [];
        this.editEventGuestsOfHonor = [];
        this.editEventSpeakers = [];
        if (event.id) {
            this.eventService.getEventParticipants(event.id).subscribe({
                next: (participants) => {
                    this.editEventCommittee = participants
                        .filter(p => p.participantType === 'COMMITTEE')
                        .map(p => this.toEditableMember(p));
                    this.editEventGuestsOfHonor = participants
                        .filter(p => p.participantType === 'GUEST_OF_HONOR')
                        .map(p => this.toEditableMember(p));
                    this.editEventSpeakers = participants
                        .filter(p => p.participantType === 'SPEAKER')
                        .map(p => this.toEditableSpeaker(p));

                    this.committeeAtLoad = this.editEventCommittee.map(m => ({...m}));
                    this.guestsAtLoad = this.editEventGuestsOfHonor.map(m => ({...m}));
                    this.speakersAtLoad = this.editEventSpeakers.map(s => ({...s}));
                },
                error: () => this.toastr.error('Failed to load event committee/speakers/guests of honor')
            });
        }
    }

    private toEditableMember(p: EventParticipantDTO): EventCommitteeMember & { id?: string } {
        return {
            id: p.id,
            memberId: p.userId,
            name: p.name,
            title: p.title,
            shortDescription: p.shortDescription,
            photoUrl: p.photoUrl,
            videoUrl: p.videoUrl
        };
    }

    private toEditableSpeaker(p: EventParticipantDTO): EventSpeaker & { id?: string } {
        return {
            id: p.id,
            speakerId: p.id,
            name: p.name,
            role: p.title,
            company: p.company,
            topic: p.topic,
            topicDescription: p.topicDescription,
            tags: p.tags,
            photoUrl: p.photoUrl,
            videoUrl: p.videoUrl
        };
    }

    private toParticipantDTO(item: any, type: EventParticipantType): EventParticipantDTO {
        if (type === 'SPEAKER') {
            return {
                participantType: type,
                name: item.name,
                title: item.role,
                company: item.company,
                topic: item.topic,
                topicDescription: item.topicDescription,
                tags: item.tags,
                photoUrl: item.photoUrl,
                videoUrl: item.videoUrl
            };
        }
        return {
            participantType: type,
            userId: type === 'COMMITTEE' ? (item.memberId || undefined) : undefined,
            name: item.name,
            title: item.title,
            shortDescription: item.shortDescription,
            photoUrl: item.photoUrl,
            videoUrl: item.videoUrl
        };
    }

    // Posts every non-blank entry from the add-event modal's committee/guest/speaker arrays as a new
    // participant, once the event (and therefore its id) exists. All adds — nothing to reconcile yet.
    private createInitialParticipants(eventId: string): void {
        const ops: Observable<any>[] = [
            ...(this.newEvent.eventCommittee || [])
                .filter(m => m.name && m.name.trim())
                .map(m => this.eventService.addEventParticipant(eventId, this.toParticipantDTO(m, 'COMMITTEE'))),
            ...(this.newEvent.eventGuestsOfHonor || [])
                .filter(m => m.name && m.name.trim())
                .map(m => this.eventService.addEventParticipant(eventId, this.toParticipantDTO(m, 'GUEST_OF_HONOR'))),
            ...(this.newEvent.eventSpeakers || [])
                .filter(s => s.name && s.name.trim())
                .map(s => this.eventService.addEventParticipant(eventId, this.toParticipantDTO(s, 'SPEAKER'))),
        ];
        if (!ops.length) return;
        forkJoin(ops).subscribe({
            error: (err) => {
                console.error('Failed to save some event committee/speaker/guest entries', err);
                this.toastr.error('Event created, but some committee/speaker/guest entries failed to save');
            }
        });
    }

    // Diffs an edited list against the ids that existed when the edit modal opened, returning one
    // add/update/delete API call per changed row.
    private buildParticipantOps(original: any[], edited: any[], eventId: string, type: EventParticipantType): Observable<any>[] {
        const ops: Observable<any>[] = [];
        const remainingIds = new Set<string>();

        for (const item of edited) {
            if (!item.name || !item.name.trim()) continue;
            const dto = this.toParticipantDTO(item, type);
            if (item.id) {
                remainingIds.add(item.id);
                ops.push(this.eventService.updateEventParticipant(eventId, item.id, dto));
            } else {
                ops.push(this.eventService.addEventParticipant(eventId, dto));
            }
        }

        for (const originalItem of original) {
            if (originalItem.id && !remainingIds.has(originalItem.id)) {
                ops.push(this.eventService.deleteEventParticipant(eventId, originalItem.id));
            }
        }

        return ops;
    }

    // Picking CONFERENCE on a new event pre-fills the default Yes/No attending question; the organizer may edit or remove it.
    onNewEventCategoryChange(): void {
        if (this.newEvent.eventCategory === 'CONFERENCE' && this.newEventQuestions.length === 0) {
            this.newEventQuestions = [defaultAttendingQuestion()];
        }
    }

    onSelectedQuestionsChange(): void {
        this.selectedQuestionsDirty = true;
    }

    addPanel(panels: TicketOptionPanel[]): void {
        panels.push({label: '', options: [], groupRequired: false});
    }

    removePanel(panels: TicketOptionPanel[], i: number): void {
        panels.splice(i, 1);
    }

    dropPanel(event: CdkDragDrop<TicketOptionPanel[]>, panels: TicketOptionPanel[]): void {
        moveItemInArray(panels, event.previousIndex, event.currentIndex);
    }

    // cdkDropList physically repositions the DOM nodes it manages as part of its drag
    // animation, ahead of Angular's own change detection. Tracking by identity (the ngFor
    // default) then fights that repositioning and desyncs the ngModel-bound inputs from their
    // panel objects. Tracking by index tells Angular to just re-bind whatever node CDK already
    // placed at each position, which matches what CDK did.
    trackByIndex(index: number): number {
        return index;
    }

    addOptionToPanel(panel: TicketOptionPanel): void {
        panel.options.push({label: '', price: '', paid: true});
    }

    removeOptionFromPanel(panel: TicketOptionPanel, i: number): void {
        panel.options.splice(i, 1);
    }

    private flattenPanels(panels: TicketOptionPanel[]): EventTicketOption[] {
        return panels.flatMap(p => p.options.map(opt => ({
            ...opt,
            sectionLabel: p.label,
            price: opt.paid === false ? '' : opt.price,
            groupRequired: !!p.groupRequired
        })));
    }

    private buildPanelsFromOptions(options: EventTicketOption[]): TicketOptionPanel[] {
        const map = new Map<string, EventTicketOption[]>();
        const order: string[] = [];
        for (const opt of options) {
            const key = opt.sectionLabel || '';
            if (!map.has(key)) { map.set(key, []); order.push(key); }
            map.get(key)!.push(opt);
        }
        return order.map(label => ({
            label,
            options: map.get(label)!,
            groupRequired: map.get(label)!.some(o => o.groupRequired)
        }));
    }

    // -------------------- Event Committee / Speakers --------------------
    addCommitteeMember(list: EventCommitteeMember[]): void {
        list.push({name: '', title: '', shortDescription: ''});
    }

    removeCommitteeMember(list: EventCommitteeMember[], i: number): void {
        list.splice(i, 1);
    }

    addSpeaker(list: EventSpeaker[]): void {
        list.push({name: '', role: '', company: '', topic: ''});
    }

    removeSpeaker(list: EventSpeaker[], i: number): void {
        list.splice(i, 1);
    }


    // In your component
    parseDate(dateStr: string | null | undefined): Date {
        if (!dateStr) return new Date(); // fallback to current date/time

        const [datePart, timePart] = dateStr.split(':', 2);
        if (!datePart || !timePart) return new Date();

        const [day, month, year] = datePart.split('/').map(Number);
        const [hour, minute, second] = timePart.split(':').map(Number);

        return new Date(year, month - 1, day, hour, minute, second);
    }


    private initializeNewEvent(): EventDTO {
        return {
            title: '',
            chapterId: '',
            msgId: '',
            eventStartDate: '',
            eventStartTime: '',
            eventEndDate: '',
            eventEndTime: '',
            eventLocation: '',
            createdBy: '',
            status: '',
            eventDescription: '',
            eventType: 'free',
            eventScope: 'CHAPTER',
            childAgeMin: '5',
            childAgeLimit: '',
            discountLabel: '',
            discountType: 'FLAT',
            discountValue: '',
            discountStartDate: '',
            discountEndDate: '',
            notifyLandingPage: false,
            stripePaymentEnabled: true,
            eventCommittee: [],
            eventGuestsOfHonor: [],
            eventSpeakers: []
        };
    }

    private initializeNewEmail(): EventEmailMessageDTO {
        return {subject: '', body: ''};
    }

    // -------------------- Events --------------------
    /*loadEvents(chapterId: string) {
      this.spinner.show();
      this.eventService.getEventsByChapterId(chapterId).pipe(
        finalize(() => this.spinner.hide())
      ).subscribe({
        next: data => {
          this.events = data.sort((a, b) => new Date(b.eventStartDate).getTime() - new Date(a.eventStartDate).getTime());
        },
        error: err => {
          console.error('Error loading events', err);
          this.toastr.error('Failed to load events');
        }
      });
    }*/


    loadEvents(chapterId: string) {
        this.spinner.show();

        this.eventService.getEventsByChapterId(chapterId).pipe(
            switchMap((data) => {
                // Sort events by start date descending
                const sortedEvents = data.sort(
                    (a, b) => new Date(b.eventStartDate).getTime() - new Date(a.eventStartDate).getTime()
                );

                // Map each event to an Observable
                const requests = sortedEvents.map(event => {
                    if (!event.createdBy) {
                        // Return an Observable immediately with default name if createdBy is null
                        return of({...event, createdByName: 'Unknown'});
                    }

                    return this.userApiService.getShortData(event.createdBy).pipe(
                        map(user => ({
                            ...event,
                            createdByName: user.firstname + ' ' + user.lastname
                        })),
                        catchError(() => of({...event, createdByName: 'Unknown'})) // fallback if API fails
                    );
                });

                // Run all requests in parallel
                return forkJoin(requests);
            }),
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (enrichedEvents) => {
                this.events = enrichedEvents; // now properly typed
                this.page = 1;                 // ✅ RESET pagination HERE
                this.events = enrichedEvents; // ✅ VALID scope
            },
            error: (err) => {
                console.error('Error loading events', err);
                this.toastr.error('Failed to load events');
            }
        });
    }


    addEvent(): void {
        const cleanedNew = cleanQuestions(this.newEventQuestions);
        if (cleanedNew.error) {
            this.toastr.warning(cleanedNew.error);
            return;
        }
        this.newEventQuestions = cleanedNew.questions;
        this.spinner.show();
        this.newEvent.chapterId = this.chapterId;
        this.newEvent.createdBy = this.userId;
        if (!this.isNecExecutive) {
            this.newEvent.eventScope = 'CHAPTER';
        } else if (!this.newEvent.eventScope) {
            this.newEvent.eventScope = 'NEC';
        }

        const flatOptions = this.flattenPanels(this.newEventPanels);
        const ccEmailIds = this.newEventCcEmailsInput
            ? this.newEventCcEmailsInput.split(',').map(e => e.trim()).filter(e => e)
            : [];
        const {eventCommittee, eventGuestsOfHonor, eventSpeakers, ...newEventFields} = this.newEvent;
        const payload: EventDTO = {
            ...newEventFields,
            eventDescription: this.normalizeRichTextToHtml(this.newEvent.eventDescription),
            ticketOptions: flatOptions.length > 0 ? flatOptions : undefined,
            registrationQuestions: this.newEventQuestions,
            ccEmailIds
        };
        if ((payload.eventType || '').toLowerCase() !== 'paid') {
            payload.childAgeMin = '';
            payload.childAgeLimit = '';
        }

        this.eventService.createEvent(payload).pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: data => {
                    this.events.unshift(data);
                    if (data.id) {
                        this.createInitialParticipants(data.id);
                    }
                    this.toastr.success('Event created successfully');
                    this.newEvent = this.initializeNewEvent();
                    this.newEventPanels = [];
                    this.newEventQuestions = [];
                    this.newEventCcEmailsInput = '';
                    this.hideAddEventModal();
                },
                error: err => {
                    console.error(err);
                    this.toastr.error(err?.error?.message || 'Failed to create event');
                }
            });
    }

    updateEvent(): void {
        if (!this.selectedEvent?.id) return;
        const eventId = this.selectedEvent.id;
        if (!(this.selectedQuestionsLegacy && !this.selectedQuestionsDirty)) {
            const cleanedEdit = cleanQuestions(this.selectedEventQuestions);
            if (cleanedEdit.error) {
                this.toastr.warning(cleanedEdit.error);
                return;
            }
            this.selectedEventQuestions = cleanedEdit.questions;
        }
        this.selectedEvent.updatedBy = this.userId;
        if (!this.isNecExecutive) {
            this.selectedEvent.eventScope = 'CHAPTER';
        }
        this.spinner.show();
        const flatEditOptions = this.flattenPanels(this.selectedEventPanels);
        const ccEmailIds = this.editEventCcEmailsInput
            ? this.editEventCcEmailsInput.split(',').map(e => e.trim()).filter(e => e)
            : [];
        const {eventCommittee, eventGuestsOfHonor, eventSpeakers, ...selectedEventFields} = this.selectedEvent;
        const payload: EventDTO = {
            ...selectedEventFields,
            eventDescription: this.normalizeRichTextToHtml(this.selectedEvent.eventDescription),
            ticketOptions: flatEditOptions.length > 0 ? flatEditOptions : undefined,
            // undefined = leave the event's questions unchanged (legacy event the organizer did not touch)
            registrationQuestions: this.selectedQuestionsLegacy && !this.selectedQuestionsDirty ? undefined : this.selectedEventQuestions,
            ccEmailIds
        };
        if ((payload.eventType || '').toLowerCase() !== 'paid') {
            payload.childAgeMin = '';
            payload.childAgeLimit = '';
        }

        const participantOps: Observable<any>[] = [
            ...this.buildParticipantOps(this.committeeAtLoad, this.editEventCommittee, eventId, 'COMMITTEE'),
            ...this.buildParticipantOps(this.guestsAtLoad, this.editEventGuestsOfHonor, eventId, 'GUEST_OF_HONOR'),
            ...this.buildParticipantOps(this.speakersAtLoad, this.editEventSpeakers, eventId, 'SPEAKER'),
        ];

        this.eventService.updateEvent(eventId, payload).pipe(
            switchMap(() => participantOps.length ? forkJoin(participantOps) : of(null)),
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: () => {
                this.toastr.success('Event updated successfully');
                this.loadEvents(this.chapterId);
                this.hideEditEventModal();
            },
            error: err => {
                console.error(err);
                this.toastr.error(err?.error?.message || 'Failed to update event');
            }
        });
    }

    deleteEvent(eventId: string | undefined): void {
        if (!eventId) {
            console.warn("Event ID is undefined. Cannot delete event.");
            this.toastr.warning('Invalid event. No ID found.');
            return;
        }

        this.spinner.show();
        this.eventService.deleteEvent(eventId).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: () => {
                this.events = this.events.filter(e => e.id !== eventId);
                this.toastr.success('Event deleted successfully');
            },
            error: err => {
                console.error(err);
                this.toastr.error('Failed to delete event');
            }
        });
    }


    // -------------------- Email Messages --------------------
    addEmail(): void {
        if (!this.selectedEvent?.id) return;
        this.spinner.show();
        this.eventService.addEmailMessage(this.selectedEvent.id, this.newEmail).pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: data => {
                    if (!this.selectedEvent!.eventRegistrations) this.selectedEvent!.eventRegistrations = [];
                    this.toastr.success('Email message added');
                    this.newEmail = this.initializeNewEmail();
                    this.hideAddEmailModal();
                },
                error: err => {
                    console.error(err);
                    this.toastr.error('Failed to add email message');
                }
            });
    }

    updateEmail(message: EventEmailMessageDTO): void {
        if (!this.selectedEvent?.id || !message.messageId) return;
        this.spinner.show();
        this.eventService.updateEmailMessage(this.selectedEvent.id, message.messageId, message).pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: data => this.toastr.success('Email message updated'),
                error: err => {
                    console.error(err);
                    this.toastr.error('Failed to update email message');
                }
            });
    }

    deleteEmail(message: EventEmailMessageDTO): void {
        if (!this.selectedEvent?.id || !message.messageId) return;
        this.spinner.show();
        this.eventService.deleteEmailMessage(this.selectedEvent.id, message.messageId).pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: () => this.toastr.success('Email message deleted'),
                error: err => {
                    console.error(err);
                    this.toastr.error('Failed to delete email message');
                }
            });
    }


    // -------------------- Modals --------------------
    hideAddEventModal(): void {
        if (this.addEventModalRef) $(this.addEventModalRef.nativeElement).modal('hide');
    }

    hideEditEventModal(): void {
        if (this.editEventModalRef) $(this.editEventModalRef.nativeElement).modal('hide');
    }

    hideAddEmailModal(): void {
        if (this.addEmailModalRef) $(this.addEmailModalRef.nativeElement).modal('hide');
    }

    private normalizeRichTextToHtml(value: any): string {
        if (value == null || value === '') return '';
        return typeof value === 'string' ? value : toHTML(value);
    }
}
